import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Stack,
  Tooltip
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  BusinessCenter as BusinessIcon,
  DateRange as DateIcon,
  FamilyRestroom as FamilyIcon,
  School as SchoolIcon,
  Wc as GenderIcon,
  OpenInNew as OpenInNewIcon
} from "@mui/icons-material";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { BASE_URL } from "app/api/axios";
import moment from "moment";

export default function FamilyMemberDetailsDialog({ open, onClose, familyMemberId }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (open && familyMemberId) {
      setLoading(true);
      setData(null);
      axiosPrivate
        .post("GetFamilyByID.php", { UID: familyMemberId })
        .then((res) => {
          if (isMounted && res.data && res.data.status === 1) {
            setData(res.data.Data[0]);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [open, familyMemberId, axiosPrivate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 3,
        background: alpha(theme.palette.background.paper, 0.9),
        backdropFilter: "blur(10px)",
        boxShadow: theme.shadows[10],
      }
    }}>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="bold">Family Member Details</Typography>
        <Stack direction="row" spacing={1}>
          {data && (
            <Tooltip title={
              (data.IsProspect === "YES" || data.IsProspect === 1 || data.IsProspect === "1" || data.IsProspect === true)
                ? "View Complete Prospect"
                : "View Complete Reference"
            }>
              <IconButton
                component="a"
                href={
                  (data.IsProspect === "YES" || data.IsProspect === 1 || data.IsProspect === "1" || data.IsProspect === true)
                    ? `/Prospects/ViewProspect/${data.ReferenceID || familyMemberId}`
                    : `/References/EditReference/${data.ReferenceID || familyMemberId}`
                }
                target="_blank"
                size="small"
                color="primary"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : data ? (
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar
                src={data.Picture ? `${BASE_URL}/GetSecureDoc?type=profile&path=${encodeURIComponent(data.Picture)}` : ""}
                sx={{ width: 80, height: 80, boxShadow: theme.shadows[3] }}
              />
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {data.Salutation ? `${data.Salutation} ` : ""}{data.Name}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {data.Relation && <Chip label={data.Relation} size="small" color="primary" variant="outlined" />}
                  {data.MaritalStatus && <Chip label={data.MaritalStatus} size="small" variant="outlined" />}
                </Stack>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {data.Mobile && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Typography variant="body2">{data.Mobile}</Typography>
                  </Box>
                </Grid>
              )}
              {data.Gender && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <GenderIcon color="secondary" fontSize="small" />
                    <Typography variant="body2">{data.Gender}</Typography>
                  </Box>
                </Grid>
              )}
              {data.Work && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="primary" fontSize="small" />
                    <Typography variant="body2">{data.Work} {data.WorkType ? `(${data.WorkType})` : ''}</Typography>
                  </Box>
                </Grid>
              )}
              {data.Education && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon color="info" fontSize="small" />
                    <Typography variant="body2">{data.Education}</Typography>
                  </Box>
                </Grid>
              )}
              {data.DOBR && data.DOBR !== "0000-00-00" && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DateIcon color="secondary" fontSize="small" />
                    <Typography variant="body2">DOB: {moment(data.DOBR).format("DD MMM YYYY")}</Typography>
                  </Box>
                </Grid>
              )}
              {data.DOM && data.DOM !== "0000-00-00" && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FamilyIcon color="error" fontSize="small" />
                    <Typography variant="body2">DOM: {moment(data.DOM).format("DD MMM YYYY")}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            {data.Workplace && (
              <Box mt={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Workplace</Typography>
                <Typography variant="body2">{data.Workplace}</Typography>
              </Box>
            )}
            {data.OldLIC && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Old LIC Policies</Typography>
                <Typography variant="body2">{data.OldLIC}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Typography color="text.secondary">No details found or failed to load.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
